import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useWebSockets } from '@/components/providers/WebsocketsProvider';
import { awsCredentialsQueries } from '@/lib/queries/integrations/cloud/aws/credentials';
import { awsRoleQueries } from '@/lib/queries/integrations/cloud/aws/roles';
import { gcpServiceAccountQueries } from '@/lib/queries/gcpServiceAccount';

export interface ScanProgress {
  servicesScannedCount: number;
  totalServicesToScan: number;
  scanStartedAt: string; // ISO timestamp
}

interface ScanSession {
  latestScanStartedAt: string;
  progress?: ScanProgress;
}

interface ScanSessionState {
  [integrationId: string]: ScanSession | undefined;
}

export interface ScanProgressState {
  [integrationId: string]: ScanProgress | undefined;
}

const parseIntegrationFromMessage = (content?: string): string => {
  if (content === undefined || content === '') {
    return 'aws'; // Default to AWS for backward compatibility
  }

  try {
    const syncData = JSON.parse(content) as {
      integration?: string;
      integration_id?: string;
    };

    return syncData.integration_id ?? syncData.integration ?? 'aws';
  } catch {
    return 'aws'; // Fallback to AWS if parsing fails
  }
};

const isNewerOrEqualScan = (
  newScanStartedAt: string,
  currentScanStartedAt?: string,
): boolean => {
  if (currentScanStartedAt === undefined) {
    return true; // No current scan, so new scan is valid
  }

  try {
    const newDate = new Date(newScanStartedAt);
    const currentDate = new Date(currentScanStartedAt);

    // Return true if new scan is newer or equal
    return newDate >= currentDate;
  } catch {
    // If timestamp parsing fails, accept the new scan
    return true;
  }
};

const getCurrentTimestamp = (): string => {
  return new Date().toISOString();
};

const invalidateQueriesForIntegration = (
  integration: string,
  queryClient: QueryClient,
  projectId: string,
): void => {
  if (integration === 'aws') {
    queryClient.invalidateQueries(awsCredentialsQueries.list(projectId));
    queryClient.invalidateQueries(awsRoleQueries.list(projectId));
  } else if (integration === 'gcp') {
    queryClient.invalidateQueries(gcpServiceAccountQueries.list(projectId));
  } else {
    // Fallback: invalidate all queries for unknown integration types
    queryClient.invalidateQueries(awsCredentialsQueries.list(projectId));
    queryClient.invalidateQueries(awsRoleQueries.list(projectId));
    queryClient.invalidateQueries(gcpServiceAccountQueries.list(projectId));
  }
};

export const useScanProgress = (projectId: string): ScanProgressState => {
  const [scanSessions, setScanSessions] = useState<ScanSessionState>({});
  const { addMessageHandler, removeMessageHandler } = useWebSockets();
  const queryClient = useQueryClient();

  // Convert scan sessions to the public progress state format
  const scanProgress: ScanProgressState = Object.fromEntries(
    Object.entries(scanSessions).map(([integrationId, session]) => [
      integrationId,
      session?.progress,
    ]),
  );

  useEffect(() => {
    addMessageHandler('api-scan-progress', (message: { content: string }) => {
      try {
        const progress = JSON.parse(message.content) as {
          total_resources_scanned: number;
          total_relationships_scanned: number;
          services_scanned_count: number;
          total_services_to_scan: number;
          integration_id?: string;
          integration?: string; // Support both fields for backward compatibility
          scan_started_at?: string; // ISO timestamp
        };

        const integrationId =
          progress.integration_id ?? progress.integration ?? 'aws';
        const scanStartedAt = progress.scan_started_at ?? getCurrentTimestamp();
        const isComplete =
          progress.services_scanned_count >= progress.total_services_to_scan;

        setScanSessions(prev => {
          const currentSession = prev[integrationId];

          // Check if this message is from the latest scan
          if (
            !isNewerOrEqualScan(
              scanStartedAt,
              currentSession?.latestScanStartedAt,
            )
          ) {
            // Ignore messages from older scans
            return prev;
          }

          const newProgress: ScanProgress = {
            servicesScannedCount: progress.services_scanned_count,
            totalServicesToScan: progress.total_services_to_scan,
            scanStartedAt,
          };

          const newSession: ScanSession = {
            latestScanStartedAt: scanStartedAt,
            progress: newProgress,
          };

          if (isComplete) {
            // Remove progress after a delay to show 100% briefly
            setTimeout(() => {
              setScanSessions(currentSessions => {
                const updatedSessions = { ...currentSessions };

                if (
                  updatedSessions[integrationId]?.latestScanStartedAt ===
                  scanStartedAt
                ) {
                  delete updatedSessions[integrationId];
                }

                return updatedSessions;
              });
            }, 1500);
          }

          return {
            ...prev,
            [integrationId]: newSession,
          };
        });
      } catch (error) {
        console.error('Failed to parse scan progress message:', error);
      }
    });

    addMessageHandler(
      'api-scan-synced',
      (message: { content?: string } = {}) => {
        const integration = parseIntegrationFromMessage(message.content);
        invalidateQueriesForIntegration(integration, queryClient, projectId);
      },
    );

    return () => {
      removeMessageHandler('api-scan-progress');
      removeMessageHandler('api-scan-synced');
    };
  }, [addMessageHandler, removeMessageHandler, queryClient, projectId]);

  return scanProgress;
};
